import { z } from "zod";
import { localeSchema } from "./locale.js";
import { singleLineTextSchema } from "./text.js";
// Impostazioni di un'azienda (piano M1a D12): un solo schema, usato alla creazione dell'azienda (Task 10)
// e dalla sezione Impostazioni (Task 12). `workingCalendar` ha la forma di `CallbackSchedulingConfig` del
// dominio senza `timeZone`, più i motivi delle chiusure (M1b) che il dominio non conosce: `schedulingConfigOf`
// costruisce `{ timeZone, weeklyHours, closedDates }` campo per campo, senza traduzioni. Le regole di validità
// sono le stesse del dominio (orari HH:mm con la fine dopo l'inizio, date YYYY-MM-DD). I contratti non
// dipendono dal dominio: i giorni omessi valgono l'orario di partenza del dominio, quindi la regola «almeno
// un giorno aperto» si controlla nel server, sul calendario completato (`hasOpenWorkingDay`).
// Calcolato una volta: l'elenco non cambia durante la vita del processo.
let supportedTimeZones;
/** `timeZone` deve essere uno dei fusi di `Intl.supportedValuesOf("timeZone")` (D12). */
export function isSupportedTimeZone(value) {
    supportedTimeZones ??= new Set(Intl.supportedValuesOf("timeZone"));
    return supportedTimeZones.has(value);
}
export const timeZoneSchema = z.string().refine(isSupportedTimeZone, { message: "Unsupported time zone" });
const CLOCK = /^([01]\d|2[0-3]):[0-5]\d$/;
/** Un orario `HH:mm` (00:00–23:59), come nel calendario: anche i confini delle mezze giornate della sequenza (`call-sequence.ts`). */
export const clockSchema = z.string().regex(CLOCK);
const clock = clockSchema;
const minutesOf = (value) => Number(value.slice(0, 2)) * 60 + Number(value.slice(3, 5));
export const workingIntervalSchema = z
    .strictObject({ start: clock, end: clock, endInclusive: z.boolean().optional() })
    .refine((interval) => minutesOf(interval.end) > minutesOf(interval.start), { message: "The interval must end after it starts", path: ["end"] });
// Un giorno assente vale l'orario di partenza del dominio; `null` è un giorno chiuso.
const day = workingIntervalSchema.nullable().optional();
export const workingCalendarSchema = z
    .strictObject({
    weeklyHours: z.strictObject({ 1: day, 2: day, 3: day, 4: day, 5: day, 6: day, 7: day }),
    // Limite tecnico sulla dimensione del corpo, non una regola di prodotto.
    closedDates: z.array(z.iso.date()).max(1000),
    // Il motivo di una chiusura (per esempio «Natale») serve soltanto a chi legge il calendario: le regole di
    // dominio non lo vedono mai (`schedulingConfigOf`). Si accetta soltanto per una data chiusa ed è un testo su una
    // riga (`text.ts`): finisce nel jsonb del calendario, che rifiuterebbe un `\u0000` con un 500.
    closureReasons: z.record(z.iso.date(), singleLineTextSchema(40)).optional(),
})
    .superRefine((calendar, ctx) => {
    const closed = new Set(calendar.closedDates);
    if (Object.keys(calendar.closureReasons ?? {}).some((date) => !closed.has(date))) {
        ctx.addIssue({ code: "custom", message: "A closure reason needs a closed date", path: ["closureReasons"] });
    }
});
export const organizationSettingsSchema = z.strictObject({
    timeZone: timeZoneSchema,
    defaultLocale: localeSchema,
    workingCalendar: workingCalendarSchema,
});
/**
 * Corpo di `PATCH /api/organization/settings` (Task 12): almeno un campo, con gli stessi schemi della
 * creazione; `workingCalendar` sostituisce il calendario intero.
 */
export const updateOrganizationSettingsBodySchema = organizationSettingsSchema
    .partial()
    .refine((body) => body.timeZone !== undefined || body.defaultLocale !== undefined || body.workingCalendar !== undefined, {
    message: "At least one field is required",
});
/** La copia autorevole delle impostazioni, con la sua versione (cresce a ogni modifica). */
export const organizationSettingsViewSchema = z.object({
    timeZone: z.string(),
    defaultLocale: localeSchema,
    workingCalendar: workingCalendarSchema,
    version: z.number().int(),
    updatedAt: z.iso.datetime(),
});
export const organizationSettingsResponseSchema = z.object({ settings: organizationSettingsViewSchema });
//# sourceMappingURL=settings.js.map